import { useState, useCallback, useEffect, useRef } from "react";
import { invoke, flushOutbox } from "../lib/bridge";
import { buildSnapshots } from "../lib/snapshots";

interface UseSyncPollOptions {
  isVisible: boolean;
}

interface UseSyncPollResult {
  syncVersion: number;
  resyncing: boolean;
  updateAvailable: boolean;
  /** Stable ref to the fulfillPendingResyncs function; safe to call from the notification socket. */
  fulfillPendingResyncsRef: React.MutableRefObject<() => void>;
  /** Stable ref to the collectResync function; safe to call from the notification socket. */
  collectResyncRef: React.MutableRefObject<() => void>;
}

/**
 * Manages the background sync poll (every 10 s), the resync requester/fulfiller
 * cycle, the outbox flush on reconnect, and the update-available check.
 */
export function useSyncPoll({ isVisible }: UseSyncPollOptions): UseSyncPollResult {
  const [syncVersion, setSyncVersion] = useState(0);
  const [resyncing, setResyncing] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // Stable refs so the notification socket can call these without re-creating its effects.
  const fulfillPendingResyncsRef = useRef<() => void>(() => {});
  const collectResyncRef = useRef<() => void>(() => {});

  // Requester side: create/collect our own resync if a pull flagged us behind the
  // relay's purge floor; bump syncVersion when fresh state lands so open docs reload.
  const collectResync = useCallback(async () => {
    try {
      const r = await invoke<{ state: string; applied: number }>("ensure_resync");
      setResyncing(r.state === "requested" || r.state === "waiting");
      if (r.state === "applied") setSyncVersion(v => v + 1);
    } catch {}
  }, []);
  collectResyncRef.current = collectResync;

  // Fulfiller side: hand fresh snapshots to any peer waiting on a resync.
  const fulfillPendingResyncs = useCallback(async () => {
    try {
      const pending = await invoke<{ id: number; requester_device_id: number }[]>("list_pending_resyncs");
      if (pending.length === 0) return;
      const snapshots = await buildSnapshots();
      if (snapshots.length === 0) return;
      for (const r of pending) {
        try {
          await invoke("fulfill_resync", { requestId: r.id, snapshots });
        } catch (e) {
          console.warn("fulfill_resync failed:", e);
        }
      }
    } catch {}
  }, []);
  fulfillPendingResyncsRef.current = fulfillPendingResyncs;

  const syncPoll = useCallback(async () => {
    try {
      const synced = await invoke<{ docId: string; updateB64: string; deleted?: boolean }[]>("pull_sync_updates");
      if (synced.length > 0) {
        setSyncVersion(v => v + 1);
      }
    } catch {}
    // A pull may have flagged us behind (409 resync_needed); drive recovery forward.
    await collectResync();
    // Eagerly cache all chat across channels (offline history) + ack delivery so
    // the relay can GC delivered messages. Fire-and-forget.
    invoke("pull_chat_messages").catch(() => {});
  }, [collectResync]);

  useEffect(() => {
    // Always poll once immediately (catch up on foreground)
    syncPoll();
    if (!isVisible) return;
    const interval = setInterval(syncPoll, 10000);
    return () => clearInterval(interval);
  }, [syncPoll, isVisible]);

  // On reconnect, flush any updates queued while offline, then catch up.
  useEffect(() => {
    const onOnline = () => {
      flushOutbox().then(syncPoll).catch(() => {});
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [syncPoll]);

  // Detect a newer deployed build and offer a reload. Re-checks whenever the app
  // regains visibility (common iOS home-screen-PWA pattern) plus periodically while
  // open. Sticky once found.
  useEffect(() => {
    if (updateAvailable || !isVisible) return;
    let cancelled = false;
    const check = () =>
      invoke<boolean>("check_update")
        .then((avail) => { if (!cancelled && avail) setUpdateAvailable(true); })
        .catch(() => {});
    check();
    const interval = setInterval(check, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isVisible, updateAvailable]);

  return { syncVersion, resyncing, updateAvailable, fulfillPendingResyncsRef, collectResyncRef };
}
